import { api } from "../lib/api";
import type { RateUnit } from "./project";

export type TimesheetPeriodType = "hours" | "percent_day" | "percent_week" | "percent_month";

/** Every field the timesheet views render is server-joined onto the row
 * (03-api-spec.md display-fields cross-cutting rule) — never fetch projects/clients
 * separately just to label a list of entries. */
export interface TimesheetEntry {
  id: string;
  project_id: string;
  invoice_id: string | null;
  date: string;
  description: string;
  period_type: TimesheetPeriodType;
  quantity: string;
  project_name: string;
  project_code: string | null;
  client_id: string;
  client_name: string;
  rate_unit: RateUnit;
  /** 3dp display conversion in the project's rate unit; null for fixed/item-rate
   * projects, whose entries attach as evidence only. Informational — the authoritative
   * rollup total is computed server-side (sum-then-round-once), not derived by summing
   * these per-row values client-side. */
  converted_quantity: string | null;
  /** Attached to an issued+ invoice — edits/deletes 409; disable the row's controls. */
  locked: boolean;
  created_at: string;
  updated_at: string;
}

export interface TimesheetEntryInput {
  date: string;
  description: string;
  period_type: TimesheetPeriodType;
  quantity: string;
}

export interface TimesheetEntryListFilters {
  date_from?: string;
  date_to?: string;
  project_id?: string;
  client_id?: string;
  uninvoiced?: boolean;
  page?: number;
  page_size?: number;
}

function buildQuery(filters: TimesheetEntryListFilters): string {
  const params = new URLSearchParams();
  if (filters.date_from) params.set("from", filters.date_from);
  if (filters.date_to) params.set("to", filters.date_to);
  if (filters.project_id) params.set("project_id", filters.project_id);
  if (filters.client_id) params.set("client_id", filters.client_id);
  if (filters.uninvoiced) params.set("uninvoiced", "true");
  if (filters.page) params.set("page", String(filters.page));
  if (filters.page_size) params.set("page_size", String(filters.page_size));
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function listTimesheetEntries(
  filters: TimesheetEntryListFilters = {},
): Promise<{ items: TimesheetEntry[]; total: number }> {
  return api.getPaged<TimesheetEntry>(`/api/timesheet-entries${buildQuery(filters)}`);
}

export function listProjectTimesheetEntries(
  projectId: string,
  filters: TimesheetEntryListFilters = {},
): Promise<{ items: TimesheetEntry[]; total: number }> {
  return api.getPaged<TimesheetEntry>(
    `/api/projects/${projectId}/timesheet-entries${buildQuery(filters)}`,
  );
}

export function listClientTimesheetEntries(
  clientId: string,
  filters: TimesheetEntryListFilters = {},
): Promise<{ items: TimesheetEntry[]; total: number }> {
  return api.getPaged<TimesheetEntry>(
    `/api/clients/${clientId}/timesheet-entries${buildQuery(filters)}`,
  );
}

export function createTimesheetEntry(
  projectId: string,
  input: TimesheetEntryInput,
): Promise<TimesheetEntry> {
  return api.post<TimesheetEntry>(`/api/projects/${projectId}/timesheet-entries`, input);
}

export function updateTimesheetEntry(
  entryId: string,
  input: TimesheetEntryInput & { updated_at: string },
): Promise<TimesheetEntry> {
  return api.put<TimesheetEntry>(`/api/timesheet-entries/${entryId}`, input);
}

export function deleteTimesheetEntry(entryId: string): Promise<void> {
  return api.delete(`/api/timesheet-entries/${entryId}`);
}
